/**
 * Zod schemas for STP (Straight-Through Processing) dry-run evaluation.
 *
 * Backend contract (opentrms StpEvaluationController, scope `stp:read`):
 *   POST /api/v1/stp/evaluate
 *   Request body: StpEvaluateRequestDto (snake_case on wire).
 *   Response body: StpDecisionDto (snake_case on wire), also embedded as the
 *   nullable `stp_decision` field on DealResponseDto (findById path only).
 *
 * BigDecimal discipline: `notional` stays a string end-to-end — never coerce
 * to number.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// StpCheckResultDto — { check_name, passed, detail }
// ---------------------------------------------------------------------------
export const StpCheckResultSchema = z.object({
  check_name: z.string(),
  passed: z.boolean(),
  detail: z.string().optional().nullable().catch(null),
});

export type StpCheckResult = z.infer<typeof StpCheckResultSchema>;

// ---------------------------------------------------------------------------
// StpDecisionDto — { rule_id, eligible, checks, reason, bypass_reason }
// `rule_id: null` means no rule matched. `reason` is defensively optional —
// this is a network boundary and the Java record field isn't annotated
// @NotNull, so treat absence safely rather than asserting it's always sent.
//
// `bypass_reason` exposes the backend's db-value string (e.g. "none",
// "manual_review"), not a fixed Java enum name — the backend may add bypass
// categories later, so this is intentionally `z.string()` rather than a
// strict enum that would throw on an unrecognized value. Defensively
// optional/nullable for legacy/older responses, defaulting to `'none'` so
// absence reads as "no bypass". Narrow against the `'manual_review'` literal
// at the call site rather than here.
// ---------------------------------------------------------------------------
export const StpDecisionSchema = z.object({
  rule_id: z.string().optional().nullable().catch(null),
  eligible: z.boolean(),
  checks: z.array(StpCheckResultSchema).catch([]),
  reason: z.string().optional().nullable().catch(null),
  bypass_reason: z.string().optional().nullable().catch('none'),
});

export type StpDecision = z.infer<typeof StpDecisionSchema>;

// ---------------------------------------------------------------------------
// StpEvaluateRequestDto — outbound request shape (snake_case on wire).
// `notional` is a BigDecimal-compatible string — never z.number().
// `product_subtype`, `currency`, `tenor_years`, `counterparty_id`,
// `portfolio_id`, `counterparty_rating` are nullable/wildcards on the
// backend — omit rather than send null when not known.
// ---------------------------------------------------------------------------
export const StpEvaluateRequestSchema = z.object({
  product_type: z.string().min(1),
  asset_class: z.string().min(1),
  product_subtype: z.string().optional(),
  currency: z.string().optional(),
  notional: z.string().min(1),
  tenor_years: z.number().int().optional(),
  counterparty_id: z.string().optional(),
  portfolio_id: z.string().optional(),
  counterparty_rating: z.string().optional(),
  compliance_pass: z.boolean(),
  ssi_exists: z.boolean(),
  extension_flags: z.record(z.string(), z.unknown()).optional(),
});

export type StpEvaluateRequest = z.infer<typeof StpEvaluateRequestSchema>;
